import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  FormControl,
  FormControlLabel,
  InputLabel,
  Input,
  InputAdornment,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Switch,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  Link,
  TextField,
} from '@mui/material';
import {
  GroupWork as GroupWorkIcon,
  Refresh as RefreshIcon,
  Share as ShareIcon,
  CheckCircle as CheckIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { groupSharingAPI } from '../services/api';
import GroupShareStatus from './posts/GroupShareStatus';
import { useClients } from '../contexts/ClientContext';
import { GROUP_SHARE_TEXT_MAX_LENGTH } from './posts/constants';

/** Nginx/Apache often default to ~60s; Test share can run minutes. HTML body = proxy error page or SPA, not FastAPI JSON. */
function formatGroupSharingHttpError(error, fallback) {
  const status = error.response?.status;
  const data = error.response?.data;
  if (typeof data === 'string' && data.trim().toLowerCase().startsWith('<!')) {
    if (status === 502 || status === 504) {
      return `${fallback}: ${status} — the reverse proxy likely timed out before the API finished (this request can take several minutes). Increase timeouts for /api (nginx: proxy_read_timeout 600s; Apache: ProxyTimeout 600) and ensure the API and Puppeteer service are running.`;
    }
    return `${fallback}: ${status || 'Error'} — server returned HTML instead of JSON. Confirm /api is proxied to FastAPI (see docs/PLESK_APACHE_PROXY_API.md).`;
  }
  const detail = data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((x) => x?.msg || x).join(', ');
  return error.message || fallback;
}

export default function GroupSharing() {
  const { clients, loading: loadingClients } = useClients();
  const [selectedClient, setSelectedClient] = useState('');
  const [facebookPages, setFacebookPages] = useState([]);
  const [selectedPageId, setSelectedPageId] = useState('');
  const [discoveredGroups, setDiscoveredGroups] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [autoShare, setAutoShare] = useState(false);
  const [defaultShareText, setDefaultShareText] = useState('');
  const [loadingPages, setLoadingPages] = useState(false);
  const [discovering, setDiscovering] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testSharing, setTestSharing] = useState(false);
  const [testSharing2, setTestSharing2] = useState(false);
  const [testSharingAll, setTestSharingAll] = useState(false);
  const [testPostUrl, setTestPostUrl] = useState('');
  const [message, setMessage] = useState({ type: '', text: '' });
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  useEffect(() => {
    if (clients.length > 0 && !selectedClient) {
      setSelectedClient(clients[0].id);
    }
  }, [clients, selectedClient]);

  useEffect(() => {
    if (!selectedClient) {
      setFacebookPages([]);
      setSelectedPageId('');
      return;
    }
    (async () => {
      try {
        setLoadingPages(true);
        const data = await groupSharingAPI.getFacebookPagesForClient(selectedClient);
        const pages = data?.pages || [];
        setFacebookPages(pages);
        if (pages.length > 0 && !selectedPageId) {
          setSelectedPageId(pages[0].id);
        } else if (pages.length === 0) {
          setSelectedPageId('');
        }
      } catch (e) {
        setMessage({ type: 'error', text: 'Failed to load Facebook pages.' });
        setFacebookPages([]);
        setSelectedPageId('');
      } finally {
        setLoadingPages(false);
      }
    })();
  }, [selectedClient]);

  // Load saved config when page is selected
  useEffect(() => {
    if (!selectedPageId) return;
    (async () => {
      try {
        const config = await groupSharingAPI.getConfig(selectedPageId);
        setSelectedGroups(config.group_ids || []);
        setAutoShare(config.auto_share === true);
        setDefaultShareText(config.default_text || '');
      } catch (e) {
        setSelectedGroups([]);
        setAutoShare(false);
        setDefaultShareText('');
      }
    })();
  }, [selectedPageId]);

  // Past share outcomes live on post.platform_specific.facebook.group_share, written by
  // the auto-share job and the manual share endpoints. They were only ever rendered on
  // the Posts page, which made this page look like nothing had run.
  const loadHistory = useCallback(async () => {
    if (!selectedPageId) {
      setHistory([]);
      return;
    }
    setLoadingHistory(true);
    try {
      const data = await groupSharingAPI.getHistory(selectedPageId, 20);
      setHistory(data?.history || []);
    } catch (e) {
      setHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  }, [selectedPageId]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const handleDiscoverGroups = async () => {
    if (!selectedPageId) {
      setMessage({ type: 'info', text: 'Select a client and Facebook page first.' });
      return;
    }
    setDiscovering(true);
    setMessage({ type: '', text: '' });
    try {
      const data = await groupSharingAPI.discover(selectedPageId);
      setDiscoveredGroups(data.groups || []);
      setMessage({ type: 'success', text: `Found ${(data.groups || []).length} group(s). Select which to share to, then click Save.` });
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || 'Discover failed.';
      setMessage({ type: 'error', text: typeof msg === 'string' ? msg : JSON.stringify(msg) });
      setDiscoveredGroups([]);
    } finally {
      setDiscovering(false);
    }
  };

  const handleSaveConfig = async () => {
    if (!selectedPageId) return;
    setSaving(true);
    setMessage({ type: '', text: '' });
    try {
      await groupSharingAPI.updateConfig(
        selectedPageId,
        selectedGroups,
        autoShare,
        defaultShareText,
      );
      setMessage({
        type: 'success',
        text: 'Saved. Auto-share, default text, and selected groups are updated.',
      });
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || 'Save failed.';
      setMessage({ type: 'error', text: typeof msg === 'string' ? msg : JSON.stringify(msg) });
    } finally {
      setSaving(false);
    }
  };

  const handleTestShare = async () => {
    if (!selectedPageId) return;
    const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';
    console.log('[GroupSharing] Test share clicked. Page:', selectedPageId, 'Post URL:', testPostUrl || '(most recent)');
    console.log('[GroupSharing] Request will go to:', `${apiBase}/group-sharing/test-share`);
    setTestSharing(true);
    setMessage({ type: '', text: '' });
    try {
      const data = await groupSharingAPI.testShare(selectedPageId, testPostUrl || null);
      console.log('[GroupSharing] Test share response:', data);
      const groupName = data?.group?.name || data?.group?.id || 'group';
      if (data?.success && data?.results?.[0]?.success) {
        setMessage({
          type: 'success',
          text: `Test share succeeded: most recent post was shared to ${groupName}. Check Facebook to confirm.`,
        });
      } else {
        const err = data?.results?.[0]?.error || data?.error || 'Share failed';
        setMessage({ type: 'error', text: `Test share: ${err}` });
      }
    } catch (e) {
      const msg = formatGroupSharingHttpError(e, 'Test share failed');
      console.error('[GroupSharing] Test share error:', e.response?.status, e.response?.data, e.message);
      setMessage({ type: 'error', text: msg });
    } finally {
      setTestSharing(false);
    }
  };

  const handleTestShare2 = async () => {
    if (!selectedPageId) return;
    setTestSharing2(true);
    setMessage({ type: '', text: '' });
    try {
      const data = await groupSharingAPI.testShare2(selectedPageId, testPostUrl || null);
      const results = data?.results || [];
      const succeeded = results.filter((r) => r?.success).length;
      const failed = results.filter((r) => !r?.success);
      if (succeeded === 2) {
        const names = (data?.groups || []).map((g) => g?.name || g?.id).join(', ');
        setMessage({
          type: 'success',
          text: `Test share to 2 groups succeeded: shared to ${names}. Check Facebook to confirm.`,
        });
      } else if (succeeded > 0) {
        setMessage({
          type: 'error',
          text: `Shared to ${succeeded} of 2 groups. Failed: ${failed.map((f) => f?.error || 'unknown').join('; ')}`,
        });
      } else {
        const err = failed[0]?.error || data?.error || 'Share failed';
        setMessage({ type: 'error', text: `Test share to 2 groups: ${err}` });
      }
    } catch (e) {
      setMessage({ type: 'error', text: formatGroupSharingHttpError(e, 'Test share to 2 groups failed') });
    } finally {
      setTestSharing2(false);
    }
  };

  const handleTestShareAll = async () => {
    if (!selectedPageId) return;
    setTestSharingAll(true);
    setMessage({ type: '', text: '' });
    try {
      const data = await groupSharingAPI.testShareAll(selectedPageId, testPostUrl || null);
      const results = data?.results || [];
      const succeeded = results.filter((r) => r?.success).length;
      const failed = results.filter((r) => !r?.success);
      const total = results.length;
      if (succeeded === total && total > 0) {
        setMessage({
          type: 'success',
          text: `Shared to all ${total} group(s). Check Facebook to confirm.`,
        });
      } else if (succeeded > 0) {
        const failedNames = failed.map((f) => f?.group_id || f?.error || 'unknown').join(', ');
        setMessage({
          type: 'error',
          text: `Shared to ${succeeded} of ${total} groups. Failed: ${failedNames}`,
        });
      } else {
        const err = failed[0]?.error || data?.error || 'Share failed';
        setMessage({ type: 'error', text: `Share to all groups: ${err}` });
      }
    } catch (e) {
      setMessage({ type: 'error', text: formatGroupSharingHttpError(e, 'Share to all groups failed') });
    } finally {
      setTestSharingAll(false);
    }
  };

  const toggleGroupSelected = (group) => {
    const id = group.id || group.name;
    setSelectedGroups((prev) =>
      prev.some((g) => (g.id || g.name) === id)
        ? prev.filter((g) => (g.id || g.name) !== id)
        : [...prev, group]
    );
  };

  const isGroupSelected = (group) => {
    const id = group.id || group.name;
    return selectedGroups.some((g) => (g.id || g.name) === id);
  };

  return (
    <Box sx={{ p: 3, maxWidth: 900, mx: 'auto' }}>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
        <GroupWorkIcon sx={{ fontSize: 32, color: 'primary.main' }} />
        <Typography variant="h5" component="h1">
          Group Sharing
        </Typography>
      </Box>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Share your Facebook Page posts to groups the Page is in. Discover groups, choose which to share to, and turn on auto-share for new posts.
      </Typography>

      {message.text && (
        <Alert
          severity={message.type === 'error' ? 'error' : message.type === 'info' ? 'info' : 'success'}
          onClose={() => setMessage({ type: '', text: '' })}
          sx={{ mb: 2 }}
        >
          {message.text}
        </Alert>
      )}

      {/* Select client & page */}
      <Paper sx={{ p: 2, mb: 2, bgcolor: 'background.paper' }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Facebook Page
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Client</InputLabel>
            <Select
              value={selectedClient}
              label="Client"
              onChange={(e) => setSelectedClient(e.target.value)}
              disabled={loadingClients}
            >
              {clients.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name || c.email || c.id}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Page</InputLabel>
            <Select
              value={selectedPageId}
              label="Page"
              onChange={(e) => setSelectedPageId(e.target.value)}
              disabled={loadingPages || facebookPages.length === 0}
            >
              {facebookPages.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.name || p.account_id || p.id}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        {facebookPages.length === 0 && selectedClient && !loadingPages && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            No Facebook page linked for this client. Connect a page in the Clients page (select client → connect Facebook page).
          </Typography>
        )}
      </Paper>

      {/* Discover groups */}
      <Paper sx={{ p: 2, mb: 2, bgcolor: 'background.paper' }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Discover groups
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Load the list of groups this Page can post to. Then choose which groups to include when sharing.
        </Typography>
        <Button
          variant="outlined"
          startIcon={discovering ? <CircularProgress size={18} /> : <RefreshIcon />}
          onClick={handleDiscoverGroups}
          disabled={discovering || !selectedPageId}
        >
          {discovering ? 'Discovering…' : 'Discover groups'}
        </Button>
        {discoveredGroups.length > 0 && (
          <List dense sx={{ mt: 2 }}>
            {discoveredGroups.map((g) => (
              <ListItem
                key={g.id || g.name}
                secondaryAction={
                  <IconButton
                    edge="end"
                    onClick={() => toggleGroupSelected(g)}
                    color={isGroupSelected(g) ? 'primary' : 'default'}
                  >
                    {isGroupSelected(g) ? <CheckIcon /> : <ShareIcon />}
                  </IconButton>
                }
              >
                <ListItemText primary={g.name || g.id} secondary={g.url ? undefined : null} />
              </ListItem>
            ))}
          </List>
        )}
      </Paper>

      {/* Groups we share to + auto-share */}
      <Paper sx={{ p: 2, mb: 2, bgcolor: 'background.paper' }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Groups we share to
        </Typography>
        {selectedGroups.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Discover groups above and select which ones to share to. You can also configure this via the API once the backend is ready.
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {selectedGroups.map((g) => (
              <Chip
                key={g.id || g.name}
                label={g.name || g.id}
                onDelete={() => setSelectedGroups((prev) => prev.filter((x) => (x.id || x.name) !== (g.id || g.name)))}
                size="small"
              />
            ))}
          </Box>
        )}
        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Default share text
        </Typography>
        <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 1 }}>
          Used for auto group share (and manual shares) when a post has no per-post override.
          Leave blank to share with an empty &quot;Say something…&quot; field — the Page caption is not
          copied.
        </Typography>
        <TextField
          fullWidth
          multiline
          minRows={3}
          value={defaultShareText}
          onChange={(e) =>
            setDefaultShareText(e.target.value.slice(0, GROUP_SHARE_TEXT_MAX_LENGTH))
          }
          placeholder="Say something about this post in groups…"
          helperText={`${defaultShareText.length.toLocaleString()} / ${GROUP_SHARE_TEXT_MAX_LENGTH.toLocaleString()}`}
        />
        <FormControlLabel
          sx={{ mt: 2 }}
          control={
            <Switch
              checked={autoShare}
              onChange={(e) => setAutoShare(e.target.checked)}
              color="primary"
            />
          }
          label="Automatically share new Page posts to these groups"
        />
        <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
          When enabled, each new post published to this Page will be shared to the selected groups (with a short delay between each).
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, mt: 2, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            onClick={handleSaveConfig}
            disabled={saving || !selectedPageId}
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
          <Button
            variant="outlined"
            onClick={handleTestShare}
            disabled={testSharing || testSharing2 || testSharingAll || !selectedPageId || selectedGroups.length === 0}
            startIcon={testSharing ? <CircularProgress size={18} /> : <ShareIcon />}
          >
            {testSharing ? 'Sharing…' : 'Test share (most recent post → 1 group)'}
          </Button>
          <Button
            variant="outlined"
            onClick={handleTestShare2}
            disabled={testSharing || testSharing2 || testSharingAll || !selectedPageId || selectedGroups.length < 2}
            startIcon={testSharing2 ? <CircularProgress size={18} /> : <ShareIcon />}
          >
            {testSharing2 ? 'Sharing…' : 'Test share to 2 groups'}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleTestShareAll}
            disabled={testSharing || testSharing2 || testSharingAll || !selectedPageId || selectedGroups.length === 0}
            startIcon={testSharingAll ? <CircularProgress size={18} /> : <ShareIcon />}
          >
            {testSharingAll ? 'Sharing to all…' : `Share to all ${selectedGroups.length} groups`}
          </Button>
        </Box>
        {(!selectedPageId || selectedGroups.length === 0) && (
          <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
            Select a Page and at least one group (and click Save) to enable Test share.
          </Typography>
        )}
        {selectedGroups.length === 1 && (
          <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
            Add a second group (and Save) to enable Test share to 2 groups.
          </Typography>
        )}
        <FormControl fullWidth sx={{ mt: 2 }}>
          <InputLabel htmlFor="test-post-url">Post URL (optional, for testing)</InputLabel>
          <Input
            id="test-post-url"
            value={testPostUrl}
            onChange={(e) => setTestPostUrl(e.target.value)}
            placeholder="https://www.facebook.com/PageName/posts/..."
          />
        </FormControl>
        <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
          If you paste a Facebook post URL above, the share will use that post. Otherwise it uses the most recent post for this Page from the app. A browser window may open; complete any CAPTCHA if prompted. Share to all groups may take several minutes depending on the number of groups.
        </Typography>
        <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
          If nothing happens when you click Test: open DevTools (F12) → Console and Network. You should see &quot;[GroupSharing] Test share clicked&quot; and the request to group-sharing/test-share. Check for errors or 502/503.
        </Typography>
      </Paper>

      {/* Recent shares */}
      <Paper sx={{ p: 2, mb: 2, bgcolor: 'background.paper' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
          <Typography variant="subtitle2" color="text.secondary">
            Recent shares
          </Typography>
          <Button
            size="small"
            startIcon={loadingHistory ? <CircularProgress size={16} /> : <RefreshIcon />}
            onClick={loadHistory}
            disabled={loadingHistory || !selectedPageId}
          >
            Refresh
          </Button>
        </Box>
        {!selectedPageId ? (
          <Typography variant="body2" color="text.secondary">
            Select a Page to see its share history.
          </Typography>
        ) : loadingHistory && history.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Loading share history…
          </Typography>
        ) : history.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No group shares recorded for this Page yet. Auto-share results appear here after
            the next Facebook post publishes.
          </Typography>
        ) : (
          <List dense disablePadding>
            {history.map((row) => (
              <ListItem
                key={row.post_id}
                divider
                sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5, py: 1.25, px: 0 }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', width: '100%' }}>
                  <Typography variant="caption" color="text.secondary">
                    {row.published_time
                      ? new Date(row.published_time).toLocaleString()
                      : `Post ${row.post_id}`}
                  </Typography>
                  {row.post_url && (
                    <Link
                      href={row.post_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="caption"
                    >
                      View post
                    </Link>
                  )}
                </Box>
                <Typography variant="body2" sx={{ width: '100%' }}>
                  {row.title || row.content_preview || `Post ${row.post_id}`}
                </Typography>
                <GroupShareStatus post={row} />
              </ListItem>
            ))}
          </List>
        )}
      </Paper>

      <Alert icon={<InfoIcon />} severity="info">
        Use &quot;Share to groups&quot; on a Facebook post in the Posts page to manually share that post to these groups.
      </Alert>
    </Box>
  );
}
